/**
 * Tab collections — filtered collection block.
 *
 * - Fetches a filtered collection URL through the Section Rendering API
 * - Swaps the block's placeholder cards for real ones the first time its tab is opened and visible
 * - The block's featured image tile is left untouched
 */

const RS_TAB_FILTERED_SECTION = 'rs-api-tab-collection-products';
const RS_TAB_FILTERED_PAGE_SIZE = 12;

if (!customElements.get('rs-tab-filtered-collection')) {
  customElements.define(
    'rs-tab-filtered-collection',
    class RsTabFilteredCollection extends HTMLElement {
      constructor() {
        super();
        this.isLoaded = false;
        this.isInViewport = false;
        this._onTabChange = () => this._loadWhenReady();
      }

      connectedCallback() {
        this.grid = this.querySelector('[data-rs-filtered-grid]');
        this.panel = this.closest('.tab-content');
        this.tabs = this.closest('tabs-component');

        if (!this.grid || !this.dataset.url) return;

        this.tabs?.addEventListener('theme:tab:change', this._onTabChange);

        this.viewportObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            this.isInViewport = true;
            this._loadWhenReady();
          },
          { rootMargin: '200px' }
        );
        this.viewportObserver.observe(this);
      }

      disconnectedCallback() {
        this.tabs?.removeEventListener('theme:tab:change', this._onTabChange);
        this.viewportObserver?.disconnect();
      }

      // Inactive tabs keep their layout box (visibility: hidden), so being in the
      // viewport is not enough on its own — the panel also has to be the current one.
      _loadWhenReady() {
        if (this.isLoaded || !this.isInViewport) return;
        if (this.panel && !this.panel.classList.contains('current')) return;

        this.isLoaded = true;
        this.viewportObserver?.disconnect();
        this.tabs?.removeEventListener('theme:tab:change', this._onTabChange);
        this._load();
      }

      async _load() {
        const limit = parseInt(this.dataset.limit, 10) || RS_TAB_FILTERED_PAGE_SIZE;
        const pageCount = Math.ceil(limit / RS_TAB_FILTERED_PAGE_SIZE);

        try {
          const pages = await Promise.all(
            Array.from({ length: pageCount }, (unused, offset) => this._fetchPage(offset + 1))
          );

          this._renderProducts(pages.join(''), limit);
        } catch (error) {
          console.error('rs-tab-filtered-collection:', error);
          this._renderEmptyState();
        }
      }

      async _fetchPage(page) {
        // Merchants may paste an absolute URL on any of the store's domains — keep the
        // path and filters but always request it from the current origin.
        const pasted = new URL(this.dataset.url, window.location.origin);
        const url = new URL(pasted.pathname + pasted.search, window.location.origin);
        url.searchParams.set('section_id', RS_TAB_FILTERED_SECTION);
        url.searchParams.set('page', page);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`${url.pathname} responded with ${response.status}`);

        return response.text();
      }

      _renderProducts(html, limit) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const items = Array.from(
          parsed.querySelectorAll('[data-rs-api-products] > [data-grid-item]')
        ).slice(0, limit);

        if (!items.length) {
          this._renderEmptyState();
          return;
        }

        const imageSizes = this.dataset.imageSizes;
        const fragment = document.createDocumentFragment();

        items.forEach((item) => {
          // The API section renders outside this section's settings and cannot work out
          // the card `sizes` itself — apply them while the images are still inert.
          if (imageSizes) {
            item.querySelectorAll('[data-product-image] img[sizes]').forEach((image) => {
              image.sizes = imageSizes;
            });
          }

          fragment.appendChild(item);
        });

        this._clearProducts();
        this.grid.appendChild(fragment);
        this._refreshSlider();
      }

      _renderEmptyState() {
        this._clearProducts();

        const wrapper = document.createElement('div');
        const paragraph = document.createElement('p');
        const strong = document.createElement('strong');

        wrapper.className = 'no-results';
        strong.textContent = this.dataset.emptyText || '';
        paragraph.appendChild(strong);
        wrapper.appendChild(paragraph);

        this.grid.appendChild(wrapper);
        this._refreshSlider();
      }

      // Removes placeholders and stale results, keeping the block's featured image tile.
      _clearProducts() {
        this.grid
          .querySelectorAll('[data-grid-item]:not(.product-item--featured), .no-results')
          .forEach((node) => node.remove());
      }

      // The slider measured the placeholders, so it has to re-read the slides.
      _refreshSlider() {
        const slider = this.querySelector('grid-slider');
        if (!slider) return;

        slider.destroy?.();
        slider.dispatchEvent(new CustomEvent('theme:grid-slider:init'));
      }
    }
  );
}
